/**
 * Smart Auto-Release settings panel (Profile page).
 *
 * Renders the master toggle + duration slider, persists to
 * `/users/me/location-automation`. The actual background tracking runs in
 * the native shell (Capacitor) — this panel is shipped behind a feature
 * flag so it's visible in dev/staging but hidden on the production PWA
 * until the native shell rollout begins.
 *
 * Reads the flag from import.meta.env.VITE_FEATURE_LOCATION_AUTOMATION.
 * The parent (ProfilePage) checks the flag and skips rendering when it's
 * off, so this component assumes it should always render.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../i18n';
import {
  getLocationAutomation,
  updateLocationAutomation,
  type LocationAutomationSettings,
} from '../api/users';
import { useAuthStore } from '../store/authStore';
import { isNativePlatform, isIOS } from '../lib/runtime';
import { autoReleasePlugin } from '../lib/autoReleasePlugin';
import { analytics } from '../hooks/useAnalytics';
import { reconcileAutoReleasePermission } from '../lib/permissionReconciliation';
import AutoReleaseRationaleDialog from './AutoReleaseRationaleDialog';

const HOURS = [1, 2, 3, 4, 5, 6, 7, 8] as const;

// The server CHECK constraint (user_loc_release_window_order) requires
// end > start. onWindowStart/onWindowEnd used to PATCH only the field the
// user touched, so pushing start past the existing end (or end before the
// existing start) silently violated the constraint and the edit appeared
// to do nothing. These helpers keep the OTHER boundary at least this many
// minutes away automatically instead of failing.
const MIN_WINDOW_MINUTES = 30;

function timeToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

function minutesToTime(mins: number): string {
  const clamped = Math.max(0, Math.min(mins, 23 * 60 + 59));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export default function LocationAutomationPanel() {
  const { t, direction } = useTranslation();
  const qc   = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [rationaleOpen, setRationaleOpen] = useState(false);
  const [permError, setPermError] = useState<string | null>(null);

  const { data: settings, isLoading } = useQuery({
    queryKey: ['location-automation'],
    queryFn:  () => getLocationAutomation().then((r) => r.data.data),
    staleTime: 5 * 60_000,
  });

  const patchMutation = useMutation({
    mutationFn: (patch: Parameters<typeof updateLocationAutomation>[0]) =>
      updateLocationAutomation(patch).then((r) => r.data.data),
    onSuccess: (data) => qc.setQueryData<LocationAutomationSettings>(['location-automation'], data),
  });

  // Re-verify the OS permission every time this panel mounts, not just at
  // app launch — catches revoking Location in device Settings and coming
  // straight back here without a full app relaunch. Refetches afterward so
  // a self-heal (releaseEnabled flipped false) shows up immediately.
  useEffect(() => {
    void reconcileAutoReleasePermission().then(() => {
      void qc.invalidateQueries({ queryKey: ['location-automation'] });
    });
  }, [qc]);

  if (isLoading || !settings) {
    return <div style={card}><div style={muted}>…</div></div>;
  }

  /**
   * Toggle ON flow:
   *   1. If we've never shown the rationale → show it; rest of flow continues
   *      from the dialog's onContinue.
   *   2. If shown previously and accepted → request OS permission directly.
   *   3. On grant → set anchor + arm the plugin + persist server-side.
   *   4. On deny → persist permission state, leave toggle off.
   *
   * Toggle OFF flow: disarm plugin + persist server-side. No prompts.
   */
  // `overrides` lets a just-changed field take effect immediately, without
  // waiting for patchMutation's round trip. Without this, arm() read the
  // stale `settings` snapshot from the last successful fetch — so every
  // edit (window, duration, weekends) re-armed the native plugin with the
  // PREVIOUS value, one edit behind, until the next full toggle/app restart.
  const armOnNative = async (overrides?: Partial<LocationAutomationSettings>) => {
    if (!isNativePlatform()) return; // PWA: server-side only
    if (!user) return;

    const effective = { ...settings, ...overrides };

    // The /users/me payload populated authStore.user.building with the anchor.
    const b = (user as any).building as
      | { id: string; latitude: number | null; longitude: number | null; timezone: string; workweekDays?: number[] }
      | null;
    if (!b?.latitude || !b?.longitude) {
      setPermError(t('auto_release_no_building_coords') || 'Building coordinates missing — contact your admin.');
      return;
    }
    await autoReleasePlugin.setBuildingAnchor({
      buildingId: b.id,
      latitude:   b.latitude,
      longitude:  b.longitude,
      timezone:   b.timezone,
    });
    // v1.1: if the user enabled the "include weekends" toggle, expand the
    // active-day set to ALL 7 days. Building.workweek_days is the source of
    // truth for which days are "workdays" (so the complement is "weekend"
    // for that building's locale — e.g. Fri+Sat for Israeli buildings).
    // The native plugin's day-of-week gate reads `workweekDays` directly,
    // so setting it to all 7 is the simplest way to disable that gate.
    const buildingWorkweek = b.workweekDays && b.workweekDays.length > 0 ? b.workweekDays : undefined;
    const effectiveWorkweek = effective.releaseIncludeWeekends ? [1, 2, 3, 4, 5, 6, 7] : buildingWorkweek;

    await autoReleasePlugin.arm({
      releaseHours:      effective.releaseHours,
      windowStartLocal:  effective.releaseWindowStartLocal ?? '06:00',
      windowEndLocal:    effective.releaseWindowEndLocal   ?? '09:00',
      // thresholdMeters omitted — native plugins hard-code the 4km release
      // distance internally. See LocationAutomationPanel comment near the
      // (removed) distance slider for rationale.
      workweekDays:      effectiveWorkweek,
      // Inherits from the Vite build env. The native worker uses this for the
      // release POST + silent token refresh — must include /api/v1.
      apiBaseUrl:        import.meta.env.VITE_API_BASE_URL || 'https://api.lotngo.com/api/v1',
    });
  };

  const finishEnable = async () => {
    setPermError(null);
    try {
      const { state } = await autoReleasePlugin.requestPermissions();
      analytics.trackAutoReleasePermission(state);
      if (state === 'granted') {
        await armOnNative();
        await patchMutation.mutateAsync({ releaseEnabled: true });
        analytics.trackAutoReleaseToggled(true);
        return;
      }
      await patchMutation.mutateAsync({ releaseEnabled: false });
      // Distinguish the three failure modes so the user knows exactly what to
      // change. `granted_when_in_use` is the most common — Android 10+ users
      // tap "While using the app" on the OS prompt (or iOS users pick "While
      // Using the App" in the system prompt), which is foreground-only and
      // useless for a geofence that has to fire while the app is closed.
      //
      // iOS variants of the error copy point at the iOS Settings hierarchy
      // ("Settings → Lot'N'Go → Location → Always") rather than the Android
      // one. The fallback `_ios` keys are picked when running natively on
      // iOS — otherwise the existing Android-flavoured strings are used.
      const ios = isIOS();
      if (state === 'granted_when_in_use') {
        setPermError(t(ios ? 'auto_release_foreground_only_ios' : 'auto_release_foreground_only'));
      } else if (state === 'denied_permanently') {
        setPermError(t(ios ? 'auto_release_denied_permanently_ios' : 'auto_release_denied_permanently'));
      } else {
        setPermError(t('auto_release_permission_denied'));
      }
    } catch (e) {
      setPermError(String((e as Error).message ?? e));
    }
  };

  const onToggle = async (next: boolean) => {
    if (next) {
      if (isNativePlatform() && !settings.rationaleShownAt) {
        analytics.trackAutoReleaseRationaleShown();
        setRationaleOpen(true);
        return;
      }
      await finishEnable();
    } else {
      await autoReleasePlugin.disarm().catch(() => {});
      await patchMutation.mutateAsync({ releaseEnabled: false });
      analytics.trackAutoReleaseToggled(false);
    }
  };

  const onHours = (next: number) => {
    patchMutation.mutate({ releaseHours: next });
    analytics.trackAutoReleaseDurationChanged(next);
    if (settings.releaseEnabled) void armOnNative({ releaseHours: next });
  };

  const onWindowStart = (next: string) => {
    const currentEnd = (settings.releaseWindowEndLocal ?? '09:00').slice(0, 5);
    let effectiveEnd = currentEnd;
    const patch: Parameters<typeof updateLocationAutomation>[0] = { releaseWindowStartLocal: next };
    if (timeToMinutes(next) >= timeToMinutes(currentEnd)) {
      effectiveEnd = minutesToTime(timeToMinutes(next) + MIN_WINDOW_MINUTES);
      patch.releaseWindowEndLocal = effectiveEnd;
    }
    patchMutation.mutate(patch);
    analytics.trackAutoReleaseWindowChanged(next, effectiveEnd);
    if (settings.releaseEnabled) void armOnNative({ releaseWindowStartLocal: next, releaseWindowEndLocal: effectiveEnd });
  };

  const onWindowEnd = (next: string) => {
    const currentStart = (settings.releaseWindowStartLocal ?? '06:00').slice(0, 5);
    let effectiveStart = currentStart;
    const patch: Parameters<typeof updateLocationAutomation>[0] = { releaseWindowEndLocal: next };
    if (timeToMinutes(next) <= timeToMinutes(currentStart)) {
      effectiveStart = minutesToTime(timeToMinutes(next) - MIN_WINDOW_MINUTES);
      patch.releaseWindowStartLocal = effectiveStart;
    }
    patchMutation.mutate(patch);
    analytics.trackAutoReleaseWindowChanged(effectiveStart, next);
    if (settings.releaseEnabled) void armOnNative({ releaseWindowStartLocal: effectiveStart, releaseWindowEndLocal: next });
  };

  // onThreshold removed — distance is hard-coded to 4km in native plugins.

  // v1.1 sub-toggles. Both re-arm the native plugin so the new settings take
  // effect immediately without waiting for the next geofence event.
  const onModeToggle = (next: boolean) => {
    patchMutation.mutate({ releaseMode: next ? 'notify' : 'auto' });
    analytics.trackAutoReleaseSubToggle('notify_mode', next);
  };

  const onWeekendsToggle = (next: boolean) => {
    patchMutation.mutate({ releaseIncludeWeekends: next });
    analytics.trackAutoReleaseSubToggle('include_weekends', next);
    if (settings.releaseEnabled) void armOnNative({ releaseIncludeWeekends: next });
  };

  const onReclaimToggle = (next: boolean) => {
    patchMutation.mutate({ returnAutoReclaim: next });
    analytics.trackAutoReleaseSubToggle('auto_reclaim', next);
  };

  const onQuietToggle = (next: boolean) => {
    patchMutation.mutate({ releaseQuietMode: next });
    analytics.trackAutoReleaseSubToggle('quiet_mode', next);
  };

  // v2 — "Pause for today". Server computes midnight-tonight in building
  // timezone; the timestamp auto-expires so no cron cleanup is needed.
  const isPausedToday = !!(
    settings.releasePausedUntil && new Date() < new Date(settings.releasePausedUntil)
  );
  const onPauseToggle = () => {
    patchMutation.mutate({ releasePausedForToday: !isPausedToday });
    analytics.trackAutoReleasePauseToday(!isPausedToday);
  };

  return (
    <>
      <div style={sectionLabel}>{t('auto_release_section_title')}</div>
      <div style={{ ...card, marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#111' }}>
              {t('auto_release_section_title')}
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
              {t('auto_release_subtitle')}
            </div>
          </div>
          <button
            role="switch"
            aria-checked={settings.releaseEnabled}
            onClick={() => onToggle(!settings.releaseEnabled)}
            disabled={patchMutation.isPending}
            style={{
              ...toggle,
              backgroundColor: settings.releaseEnabled ? '#16a34a' : '#d1d5db',
              cursor: patchMutation.isPending ? 'not-allowed' : 'pointer',
              opacity: patchMutation.isPending ? 0.6 : 1,
            }}
          >
            <span
              style={{
                ...toggleThumb,
                transform: settings.releaseEnabled
                  ? direction === 'rtl' ? 'translateX(-20px)' : 'translateX(20px)'
                  : 'translateX(2px)',
              }}
            />
          </button>
        </div>

        {settings.releaseEnabled && (
          <div style={{ marginTop: '16px', borderTop: '1px solid #f3f4f6', paddingTop: '16px' }}>

            {/* v1.1 — Mode toggle (auto vs notify-and-confirm). */}
            <div style={subToggleRow}>
              <div style={{ flex: 1 }}>
                <div style={subToggleLabel}>{t('auto_release_mode_notify_label')}</div>
                <div style={subToggleHelp}>{t('auto_release_mode_notify_help')}</div>
              </div>
              <button
                role="switch"
                aria-checked={settings.releaseMode === 'notify'}
                onClick={() => onModeToggle(settings.releaseMode !== 'notify')}
                disabled={patchMutation.isPending}
                style={{
                  ...toggle,
                  backgroundColor: settings.releaseMode === 'notify' ? '#16a34a' : '#d1d5db',
                  cursor: patchMutation.isPending ? 'not-allowed' : 'pointer',
                  opacity: patchMutation.isPending ? 0.6 : 1,
                }}
              >
                <span style={{
                  ...toggleThumb,
                  transform: settings.releaseMode === 'notify'
                    ? direction === 'rtl' ? 'translateX(-20px)' : 'translateX(20px)'
                    : 'translateX(2px)',
                }}/>
              </button>
            </div>

            {/* v1.1 — Include weekends toggle. The "weekend" definition comes
                from the COMPLEMENT of the building's workweek_days, not from
                the user's UI language — so a Hebrew building gets Fri+Sat
                added, a Western building gets Sat+Sun. The native plugin's
                day gate just receives the resulting workweekDays array. */}
            <div style={{ ...subToggleRow, marginTop: '12px' }}>
              <div style={{ flex: 1 }}>
                <div style={subToggleLabel}>{t('auto_release_weekends_label')}</div>
                <div style={subToggleHelp}>{t('auto_release_weekends_help')}</div>
              </div>
              <button
                role="switch"
                aria-checked={settings.releaseIncludeWeekends}
                onClick={() => onWeekendsToggle(!settings.releaseIncludeWeekends)}
                disabled={patchMutation.isPending}
                style={{
                  ...toggle,
                  backgroundColor: settings.releaseIncludeWeekends ? '#16a34a' : '#d1d5db',
                  cursor: patchMutation.isPending ? 'not-allowed' : 'pointer',
                  opacity: patchMutation.isPending ? 0.6 : 1,
                }}
              >
                <span style={{
                  ...toggleThumb,
                  transform: settings.releaseIncludeWeekends
                    ? direction === 'rtl' ? 'translateX(-20px)' : 'translateX(20px)'
                    : 'translateX(2px)',
                }}/>
              </button>
            </div>

            {/* v2 — Auto-reclaim on return. Silently closes any auto-release
                free window and blocks any active schedule slot the moment
                the user returns home. Defaults ON because it's the natural
                companion to releasing — "give it up when I leave, take it
                back when I'm home." */}
            <div style={{ ...subToggleRow, marginTop: '12px' }}>
              <div style={{ flex: 1 }}>
                <div style={subToggleLabel}>{t('auto_release_reclaim_label')}</div>
                <div style={subToggleHelp}>{t('auto_release_reclaim_help')}</div>
              </div>
              <button
                role="switch"
                aria-checked={settings.returnAutoReclaim}
                onClick={() => onReclaimToggle(!settings.returnAutoReclaim)}
                disabled={patchMutation.isPending}
                style={{
                  ...toggle,
                  backgroundColor: settings.returnAutoReclaim ? '#16a34a' : '#d1d5db',
                  cursor: patchMutation.isPending ? 'not-allowed' : 'pointer',
                  opacity: patchMutation.isPending ? 0.6 : 1,
                }}
              >
                <span style={{
                  ...toggleThumb,
                  transform: settings.returnAutoReclaim
                    ? direction === 'rtl' ? 'translateX(-20px)' : 'translateX(20px)'
                    : 'translateX(2px)',
                }}/>
              </button>
            </div>

            {/* v2 — Quiet mode. Suppresses the post-release cancel push so it
                doesn't interrupt driving / CarPlay navigation. Greyed out
                when notify mode is on, since notify mode's whole purpose is
                a push notification — silencing it would break the feature. */}
            <div style={{ ...subToggleRow, marginTop: '12px', opacity: settings.releaseMode === 'notify' ? 0.45 : 1 }}>
              <div style={{ flex: 1 }}>
                <div style={subToggleLabel}>{t('auto_release_quiet_label')}</div>
                <div style={subToggleHelp}>
                  {settings.releaseMode === 'notify'
                    ? t('auto_release_quiet_disabled_in_notify')
                    : t('auto_release_quiet_help')}
                </div>
              </div>
              <button
                role="switch"
                aria-checked={settings.releaseQuietMode}
                onClick={() => settings.releaseMode !== 'notify' && onQuietToggle(!settings.releaseQuietMode)}
                disabled={patchMutation.isPending || settings.releaseMode === 'notify'}
                style={{
                  ...toggle,
                  backgroundColor: settings.releaseQuietMode ? '#16a34a' : '#d1d5db',
                  cursor: (patchMutation.isPending || settings.releaseMode === 'notify') ? 'not-allowed' : 'pointer',
                  opacity: (patchMutation.isPending || settings.releaseMode === 'notify') ? 0.6 : 1,
                }}
              >
                <span style={{
                  ...toggleThumb,
                  transform: settings.releaseQuietMode
                    ? direction === 'rtl' ? 'translateX(-20px)' : 'translateX(20px)'
                    : 'translateX(2px)',
                }}/>
              </button>
            </div>

            {/* v2 — Pause for today. A one-shot action button, visually distinct
                from the persistent sub-toggles above. Shows an amber "paused"
                state with a Resume button when active. Auto-expires at midnight
                server-side so no manual reset is needed after a sick day. */}
            <div style={{ ...subToggleRow, marginTop: '12px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ ...subToggleLabel, color: isPausedToday ? '#d97706' : '#111' }}>
                  {isPausedToday ? t('auto_release_paused_until') : t('auto_release_pause_today')}
                </div>
                {!isPausedToday && (
                  <div style={subToggleHelp}>{t('auto_release_pause_today_help')}</div>
                )}
              </div>
              <button
                onClick={onPauseToggle}
                disabled={patchMutation.isPending}
                style={{
                  padding:         '5px 12px',
                  borderRadius:    '8px',
                  border:          `1.5px solid ${isPausedToday ? '#d97706' : '#d1d5db'}`,
                  backgroundColor: isPausedToday ? '#fef3c7' : '#f9fafb',
                  color:           isPausedToday ? '#92400e' : '#374151',
                  fontSize:        '12px',
                  fontWeight:      600,
                  cursor:          patchMutation.isPending ? 'not-allowed' : 'pointer',
                  opacity:         patchMutation.isPending ? 0.6 : 1,
                  flexShrink:      0,
                  whiteSpace:      'nowrap',
                  transition:      'background-color 0.15s, border-color 0.15s',
                }}
              >
                {isPausedToday ? t('auto_release_resume') : t('auto_release_pause_today')}
              </button>
            </div>

            <div style={{ borderTop: '1px solid #f3f4f6', marginTop: '16px', paddingTop: '16px' }} />

            <label style={{ display: 'block', fontSize: '13px', color: '#374151', marginBottom: '8px' }}>
              {t('auto_release_duration_label')}: {t('auto_release_hours').replace('{n}', String(settings.releaseHours))}
            </label>
            <input
              type="range"
              min={HOURS[0]}
              max={HOURS[HOURS.length - 1]}
              step={1}
              value={settings.releaseHours}
              onChange={(e) => onHours(parseInt(e.target.value, 10))}
              disabled={patchMutation.isPending}
              style={{ width: '100%' }}
            />

            {/* Window pickers — per-user override of the building defaults.
                Empty fields fall back to building defaults server-side. */}
            <label style={{ display: 'block', fontSize: '13px', color: '#374151', marginTop: '16px', marginBottom: '8px' }}>
              {t('auto_release_window_label')}
            </label>
            {/* Postgres TIME columns serialise as "HH:MM:SS" (sometimes
                with sub-second precision). `<input type="time">` is happier
                with "HH:MM" — and the bare label below was rendering the
                full string ("06:00:00–09:00:00"). Slice to 5 chars at
                every render site so the UI stays clean regardless of what
                the API returns. */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="time"
                value={(settings.releaseWindowStartLocal ?? '06:00').slice(0, 5)}
                onChange={(e) => onWindowStart(e.target.value)}
                disabled={patchMutation.isPending}
                style={timeInputStyle}
              />
              <span style={{ color: '#9ca3af' }}>–</span>
              <input
                type="time"
                value={(settings.releaseWindowEndLocal ?? '09:00').slice(0, 5)}
                onChange={(e) => onWindowEnd(e.target.value)}
                disabled={patchMutation.isPending}
                style={timeInputStyle}
              />
            </div>

            {/* Distance slider removed — release distance is hard-coded to
                4km in the native plugins. Rationale: lower distances need a
                vehicle-motion gate to suppress false positives from morning
                walks/runs, which adds significant complexity (and caused
                real false-negatives in user testing — see autoReleaseService
                history). 4km is the smallest distance where "couldn't have
                walked here in the morning window" reliably holds, so the
                vehicle gate becomes unnecessary. The releaseThresholdM column
                stays in DB + API for backward compat but is no longer surfaced
                or sent from this UI. */}
          </div>
        )}

        {/* Window note shows the user's CURRENT effective window — not the
            hardcoded 06–09 default. Hidden when the master toggle is OFF
            because the window is meaningless then. The time range is wrapped
            in a dir="ltr" span so digits + colon + en-dash render naturally
            inside a Hebrew (RTL) parent paragraph. Without the wrapper,
            "06:00–09:00" can render in confusing visual order on some
            engines. */}
        {settings.releaseEnabled && (
          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '12px' }}>
            {(() => {
              // Slice to 5 chars — Postgres TIME returns "HH:MM:SS" which
              // would render as "06:00:00–09:00:00" in the note. We only
              // care about hours+minutes here.
              const start = (settings.releaseWindowStartLocal ?? '06:00').slice(0, 5);
              const end   = (settings.releaseWindowEndLocal   ?? '09:00').slice(0, 5);
              const note  = t('auto_release_window_note');
              const [before, after] = note.split('{window}');
              return (
                <>
                  {before}
                  <span dir="ltr" style={{ unicodeBidi: 'isolate' }}>
                    {start}–{end}
                  </span>
                  {after}
                </>
              );
            })()}
          </div>
        )}

        {permError && (
          <div style={{ fontSize: '12px', color: '#b91c1c', marginTop: '8px' }}>{permError}</div>
        )}
      </div>

      <AutoReleaseRationaleDialog
        open={rationaleOpen}
        onContinue={async () => {
          setRationaleOpen(false);
          analytics.trackAutoReleaseRationaleAccepted();
          await patchMutation.mutateAsync({ rationaleDeclinedAt: null } as any);
          await finishEnable();
        }}
        onDecline={async () => {
          setRationaleOpen(false);
          analytics.trackAutoReleaseRationaleDeclined();
          await patchMutation.mutateAsync({ releaseEnabled: false, rationaleDeclinedAt: new Date().toISOString() } as any);
        }}
      />
    </>
  );
}

const card: React.CSSProperties = {
  backgroundColor: '#ffffff',
  borderRadius:    '14px',
  boxShadow:       '0 12px 32px -4px rgba(43,52,55,0.06)',
  padding:         '14px 16px',
};

const sectionLabel: React.CSSProperties = {
  fontSize:      '12px',
  fontWeight:    600,
  color:         '#9ca3af',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  marginBottom:  '6px',
};

const muted: React.CSSProperties = {
  padding: '16px 18px',
  color:   '#586064',
  fontSize:'13px',
};

const timeInputStyle: React.CSSProperties = {
  flex:         1,
  padding:      '8px 10px',
  borderRadius: '8px',
  border:       '1px solid #e5e7eb',
  fontSize:     '14px',
  fontFamily:   "'Inter', system-ui, sans-serif",
  color:        '#111',
  background:   '#fff',
};

// Mirrors the master-push toggle in NotificationPreferencesPanel so the two
// settings rows read as a matched pair on the Profile page.
const toggle: React.CSSProperties = {
  position:     'relative',
  display:      'inline-flex',
  alignItems:   'center',
  width:        '44px',
  height:       '26px',
  borderRadius: '13px',
  border:       'none',
  flexShrink:   0,
  transition:   'background-color 0.2s',
  padding:      0,
};

const toggleThumb: React.CSSProperties = {
  position:        'absolute',
  width:           '22px',
  height:          '22px',
  borderRadius:    '50%',
  backgroundColor: '#fff',
  boxShadow:       '0 1px 3px rgba(0,0,0,0.3)',
  transition:      'transform 0.2s',
};

// v1.1 sub-toggle row styles — smaller than master toggle, sits underneath.
const subToggleRow: React.CSSProperties = {
  display:        'flex',
  alignItems:     'center',
  justifyContent: 'space-between',
  gap:            '12px',
};
const subToggleLabel: React.CSSProperties = {
  fontSize:   '13px',
  fontWeight: 600,
  color:      '#111',
};
const subToggleHelp: React.CSSProperties = {
  fontSize:  '11px',
  color:     '#6b7280',
  marginTop: '2px',
};
